/*
  Free-gift panel on the cart page (snippets/pujalane-free-gift.liquid).

  Three jobs:
    - Add the gift a shopper picks, or swap the one already in the cart for
      another, then redraw the cart from the sections Shopify sends back — the
      same ones cart.js redraws after a quantity change.
    - Keep the panel itself current. It sits outside .js-contents, which is all
      cart.js redraws, so after every other cart change it is fetched again.
    - Celebrate the moment the gift unlocks: the panel pops, confetti bursts
      from it and, if the panel is scrolled out of view, a pill at the top of
      the screen says so and scrolls back to it.

  The panel cannot know whether a cart truly qualifies (see the snippet), so a
  gift that was added but did not come out free is taken straight back out
  rather than left in the cart at full price.

  Loaded with defer AFTER constants.js, pubsub.js and cart.js, whose globals are
  bare consts in classic scripts — hence the `typeof` guards.
*/
(function () {
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var busy = false;
  var selfPublished = false;

  function panel() {
    return document.querySelector('[data-pl-free-gift]');
  }

  function post(path, body) {
    return fetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json().then(function (json) {
        if (!response.ok) throw json;
        return json;
      });
    });
  }

  function cartItems() {
    var element = document.querySelector('cart-items');
    return element && element.getSectionsToRender ? element : null;
  }

  function swapPanel(html) {
    var current = panel();
    var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-pl-free-gift]');
    if (current && fresh) current.innerHTML = fresh.innerHTML;
    noteState();
  }

  /* ---------- unlock celebration ---------- */

  // Remembered for the browsing session, so the celebration plays once per
  // unlock — not on every quantity change or every return to the cart — and
  // plays again if the cart drops below the threshold and climbs back.
  var CELEBRATED = 'pl-gift-celebrated';

  function remembered(value) {
    try {
      if (value === undefined) return window.sessionStorage.getItem(CELEBRATED) === '1';
      if (value) window.sessionStorage.setItem(CELEBRATED, '1');
      else window.sessionStorage.removeItem(CELEBRATED);
    } catch (error) {
      // Storage blocked: the celebration simply plays whenever the state is met.
    }
    return false;
  }

  function noteState() {
    var current = panel();
    var gift = current && current.querySelector('[data-pl-gift-state]');
    if (!gift) return;

    var state = gift.dataset.plGiftState;
    if (state === 'locked') {
      remembered(false);
      return;
    }
    if (remembered()) return;
    remembered(true);
    // A gift already sitting in the cart was claimed on an earlier visit.
    if (state === 'unlocked') celebrate(gift);
  }

  function celebrate(gift) {
    var rect = gift.getBoundingClientRect();
    var visible = rect.bottom > 0 && rect.top < window.innerHeight;
    var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!still) {
      gift.classList.add('pl-gift--pop');
      confetti(visible ? rect.left + rect.width / 2 : window.innerWidth / 2, visible ? rect.top + 24 : 48);
    }
    if (!visible) toast();
  }

  function confetti(originX, originY) {
    if (!document.body.animate) return;
    var colours = ['#1a7f37', '#f3b21a', '#c2281c', '#5b3a1f', '#ffe9a8', '#3aa856'];

    for (var index = 0; index < 44; index++) {
      var piece = document.createElement('span');
      piece.className = 'pl-gift-confetti';
      piece.style.background = colours[index % colours.length];
      if (index % 3 === 0) piece.style.borderRadius = '50%';
      document.body.appendChild(piece);

      // Thrown up and outwards, then left to fall.
      var spread = (Math.random() - 0.5) * Math.min(window.innerWidth, 520);
      var rise = 60 + Math.random() * 130;
      var fall = 160 + Math.random() * 220;
      var spin = (Math.random() - 0.5) * 900;
      var from = 'translate(' + originX + 'px,' + originY + 'px) ';

      piece.animate(
        [
          { transform: from + 'rotate(0deg) scale(0.4)', opacity: 1 },
          {
            transform: 'translate(' + (originX + spread * 0.7) + 'px,' + (originY - rise) + 'px) rotate(' + spin * 0.5 + 'deg) scale(1)',
            opacity: 1,
            offset: 0.35,
          },
          {
            transform: 'translate(' + (originX + spread) + 'px,' + (originY + fall) + 'px) rotate(' + spin + 'deg) scale(0.9)',
            opacity: 0,
          },
        ],
        { duration: 1300 + Math.random() * 700, easing: 'cubic-bezier(0.2, 0.7, 0.4, 1)', fill: 'forwards' }
      ).onfinish = (function (element) {
        return function () {
          element.remove();
        };
      })(piece);
    }
  }

  function toast() {
    var old = document.querySelector('.pl-gift-toast');
    if (old) old.remove();

    var pill = document.createElement('button');
    pill.type = 'button';
    pill.className = 'pl-gift-toast';
    pill.textContent = 'FREE gift unlocked! Tap to choose';
    document.body.appendChild(pill);

    function dismiss() {
      pill.classList.remove('is-shown');
      window.setTimeout(function () {
        pill.remove();
      }, 400);
    }

    pill.addEventListener('click', function () {
      var current = panel();
      if (current) current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      dismiss();
    });

    // Next frame, so the slide-in transition has a starting position.
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        pill.classList.add('is-shown');
      });
    });
    window.setTimeout(dismiss, 6000);
  }

  // Redraws what cart.js redraws after a quantity change, plus the panel.
  function applySections(sections) {
    var items = cartItems();
    if (!items || !sections) return;

    items.getSectionsToRender().forEach(function (section) {
      var html = sections[section.section];
      var holder = document.getElementById(section.id);
      if (!html || !holder) return;
      var target = holder.querySelector(section.selector) || holder;
      target.innerHTML = items.getSectionInnerHTML(html, section.selector);
    });

    var main = document.getElementById('main-cart-items');
    if (main && sections[main.dataset.id]) swapPanel(sections[main.dataset.id]);

    // Lets the sticky checkout bar and the cart icon catch up. Sent as
    // 'cart-items' so that cart.js does not redraw everything a second time.
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      selfPublished = true;
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
      selfPublished = false;
    }
  }

  function refreshPanel() {
    var main = document.getElementById('main-cart-items');
    if (!main || !panel()) return;
    fetch(root + 'cart?section_id=' + main.dataset.id)
      .then(function (response) {
        return response.text();
      })
      .then(swapPanel)
      .catch(function () {});
  }

  function showMessage(text) {
    var current = panel();
    var message = current && current.querySelector('[data-pl-gift-message]');
    if (!message) return;
    message.textContent = text;
    message.hidden = false;
  }

  function choose(button) {
    var items = cartItems();
    if (!items) return;

    var variantId = Number(button.dataset.plGiftAdd);
    var swapKey = button.dataset.plGiftSwap;
    var sections = items.getSectionsToRender().map(function (section) {
      return section.section;
    });
    var redraw = { sections: sections, sections_url: window.location.pathname };

    busy = true;
    panel().classList.add('is-busy');
    button.setAttribute('aria-busy', 'true');

    // Swapping: the gift already in the cart comes out first.
    var start = swapKey ? post('cart/change.js', { id: swapKey, quantity: 0 }) : Promise.resolve();

    start
      .then(function () {
        return post('cart/add.js', Object.assign({ items: [{ id: variantId, quantity: 1 }] }, redraw));
      })
      .then(function (added) {
        return fetch(root + 'cart.js')
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            var lines = cart.items.filter(function (item) {
              return item.variant_id === variantId;
            });
            var free = lines.some(function (item) {
              return item.final_line_price < item.original_line_price;
            });
            if (free || !lines.length) return { sections: added.sections, free: free };

            // The discount did not apply: do not leave the "gift" in at full price.
            return post(
              'cart/change.js',
              Object.assign({ id: lines[0].key, quantity: lines[0].quantity - 1 }, redraw)
            ).then(function (changed) {
              return { sections: changed.sections, free: false };
            });
          });
      })
      .then(function (result) {
        applySections(result.sections);
        if (!result.free) {
          showMessage('This cart does not qualify for the free gift yet, so it was not added.');
        }
      })
      .catch(function () {
        // Whatever state the cart is in, a reload shows it truthfully.
        window.location.reload();
      })
      .then(function () {
        busy = false;
        var current = panel();
        if (current) current.classList.remove('is-busy');
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-pl-gift-add]');
    if (!button || busy || !panel() || !panel().contains(button)) return;
    choose(button);
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      if (!selfPublished) refreshPanel();
    });
  }

  // Arriving at the cart already over the threshold counts as unlocking too.
  // Held back briefly so it is not spent underneath the mobile loading screen.
  window.setTimeout(noteState, 900);
})();
